/**
 * «میزهای من» ِ فروشگاه — کاربر، ۲۱ اکتبر ۲۰۲۶: «مشتری میزهایی را که برای خودش می‌سازد بخرد؛ همه‌ی
 * ساختن (شهر، فرش، پشتِ کارت) در فروشگاه باشد؛ میزهایش را آن‌جا ببیند و مدیریت کند و سرِ ساختنِ اتاق
 * به کار ببرد».
 *
 * دو حالت: فهرستِ میزهای ساخته‌شده (پیش‌نمایش، نام، ویرایش، حذف) و سازنده — سه ردیف و یک پیش‌نمایش.
 * هر شهری با هر فرشی: روی پیش‌نمایش (و سرِ میزِ گوشی) فرشِ انتخابی با پرسپکتیو روی فرشِ نقاشی‌شده‌ی
 * شهر می‌نشیند — همان کارِ scene/table.ts. انتخاب اول امتحان است؛ ذخیره‌ی ترکیب رایگان است (سقفش در
 * سرور).
 *
 * از ۳۰ اکتبرِ ۲۰۲۶ وب «فعلاً» چیزی نمی‌فروشد (کاربر: همه‌ی خرید در اپ). میز فقط از قلم‌های داشته ساخته
 * می‌شود: قلمِ نداشته «در اپ بخر» می‌گیرد، و تا همه‌اش داشته نشده، دکمه‌ی پایین به بخشِ «در اپ بخر»
 * ِ همین صفحه می‌رود و میز ذخیره نمی‌شود. هیچ خریدی از این‌جا انجام نمی‌شود.
 *
 * خریدنی‌ها: شهر (`carpet_<شهر>` — فرشِ خودش را همراه دارد)، فرشِ تنها (`rug_<کلید>`؛ ایوان، ماهوت و
 * نمد رایگان) و پشتِ کارت (گوهری). سرور داشتن را دوباره می‌سنجد (economy.OwnsTable).
 *
 * اتاق: «بیشتر» ِ ساختنِ اتاق یکی از همین میزها را می‌گیرد (TablePicker.tsx) و همه‌ی سرِ میز همان
 * شهر و فرش و پشت را می‌بینند. بازیِ سریع همان قرعه است.
 *
 * جا: صفحه‌ی فروشگاهِ سایت (/shop.html، site/shop) — کاربر، ۲۲ اکتبر ۲۰۲۶: «همه‌چیز در خودِ فروشگاه؛
 * «میزِ تازه» هم همان‌جا». ذخیره با REST ِ [[shop]] است، نه سوکتِ بازی. /play فقط
 * TablePreview و myTableName را برای انتخاب‌گرِ اتاق برمی‌دارد.
 */
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import type { MyTableRow } from "../net/client";
import { signal, useSignal } from "../net/signal";
import { backArt } from "../game/cardBack";
import { carpets, FREE_RUGS, rugName, SCENE_FRAME, TABLES_LOADED, tableArt, tableKey, tableOf, tableTitle, thumbOf, viewOf, type TableDef } from "../game/carpet";
import { quadTransform, type Pt } from "../scene/homography";
import { TALL_RUG_ASPECT } from "../scene/geometry";
import { ask, notify } from "./Dialog";
import { APP_BUY, type CatalogItem } from "../shop/catalog";
import { shop } from "../shop/store";
import "./mytables.css";

/** سقفِ «میزهای من» — آینه‌ی MaxPlayerTables ِ سرور. */
export const MAX_TABLES = 12;

/** «با این شهر میز بساز» ِ ویترین: سازنده با همین شهر (و فرشِ خودش) باز می‌شود. */
export const buildRequest = signal<{ city: string; n: number } | null>(null);

const LOGIN = "/account.html?next=%2Fshop.html";

function useCatalogTables(): TableDef[] {
  const [list, setList] = useState(() => [...carpets]);
  useEffect(() => {
    const on = () => setList([...carpets]);
    addEventListener(TABLES_LOADED, on);
    return () => removeEventListener(TABLES_LOADED, on);
  }, []);
  return list;
}

/** نامِ میزِ ساخته‌شده: نامی که مشتری گذاشته، وگرنه از محتوایش. */
export function myTableName(t: MyTableRow): string {
  return t.name.trim() || tableTitle(t.table);
}

/** پیش‌نمایشِ گوشی‌شکلِ میز: پرده، فرش (تخت یا با پرسپکتیو) و سه ورقِ پشت‌رو. */
export function TablePreview({ city, rug, back, w = 216 }: { city: string; rug: string; back: string; w?: number }) {
  const v = viewOf(tableKey(city, rug));
  const W = w, H = Math.round(w * 16 / 9);
  let rugEl: ReactNode = null;
  if (v.city && v.rug !== v.city && v.city.corners?.length === 8) {
    const F = SCENE_FRAME, c = v.city.corners, s = Math.max(W / F.w, H / F.h);
    const x0 = (W - F.w * s) / 2, y0 = (H - F.h * s) * 0.54;
    const quad = [0, 2, 4, 6].map((i) => [x0 + c[i]! * s, y0 + c[i + 1]! * s] as Pt);
    const bw = 400, bh = Math.round(400 / TALL_RUG_ASPECT);
    rugEl = <i className="mt-proj" style={{ width: bw, height: bh, transform: quadTransform(bw, bh, quad), background: rugBg(v.rug) }} />;
  } else if (!v.city) {
    const oval = !!v.rug.round || v.rug.kind === "felt";
    const tall = v.rug.kind === "scene";
    const rw = tall ? W * 0.62 : W * 0.78, rh = tall ? rw / TALL_RUG_ASPECT : oval ? rw * 0.92 : rw;
    rugEl = <i className={"mt-flat" + (oval ? " oval" : "")} style={{ width: rw, height: rh, background: rugBg(v.rug) }} />;
  }
  const src = backArt(back) || "/art/table/back.webp";
  return (
    <div className="mt-preview" aria-hidden="true"
         style={{ "--pw": `${W}px`, "--ph": `${H}px`, "--cw": `${Math.round(W * 0.16)}px`,
                  background: v.city ? `${v.city.bg} url("${v.city.src ?? tableArt(v.city.file)}") center 54% / cover no-repeat` : v.rug.bg } as CSSProperties}>
      {rugEl}
      <div className="mt-cards">
        {[-1, 0, 1].map((k) => <img key={k} src={src} alt="" style={{ transform: `rotate(${k * 12}deg) translateY(${Math.abs(k) * 6}px)` }} />)}
      </div>
    </div>
  );
}

function rugBg(r: TableDef): string {
  if (r.kind === "felt") return r.felt ?? r.bg;
  const url = r.kind === "scene" ? (r.tallSrc ?? tableArt(`tall-${r.key}.webp`)) : `/art/table/${r.file}`;
  return `url("${url}") center / 100% 100% no-repeat`;
}

/** فهرستِ «میزهای من» با سازنده‌ی میزِ تازه و ویرایش. */
export function MyTables({ items, busy, setBusy, registered }: {
  items: CatalogItem[] | null; busy: string; setBusy: (s: string) => void; registered: boolean;
}) {
  const tables = useSignal(shop.myTables);
  const [editing, setEditing] = useState<MyTableRow | "new" | null>(null);
  const [preset, setPreset] = useState("");
  const want = useSignal(buildRequest);
  useEffect(() => { if (registered) void shop.loadMyTables(); }, [registered]);
  useEffect(() => {
    if (!want || !registered) return;
    setPreset(want.city);
    setEditing("new");
  }, [want, registered]);
  const byId = useMemo(() => new Map((items ?? []).map((i) => [i.id, i])), [items]);

  if (editing) {
    return (
      <Builder key={editing === "new" ? `new-${want?.n ?? 0}` : editing.id} initial={editing === "new" ? undefined : editing}
               preset={editing === "new" ? preset : ""}
               items={items} busy={busy} setBusy={setBusy} registered={registered}
               onDone={() => { setEditing(null); setPreset(""); buildRequest.value = null; }} />
    );
  }

  async function remove(t: MyTableRow) {
    const yes = await ask({ title: `«${myTableName(t)}» پاک شود؟`, body: "شهر و فرش و پشتی که خریده‌ای مال خودت می‌مانند؛ فقط این ترکیب از فهرست می‌رود.", ok: "پاکش کن", danger: true });
    if (!yes) return;
    const e = await shop.deleteMyTable(t.id);
    notify(e ? "پاک نشد؛ دوباره امتحان کن" : "میز پاک شد");
  }

  const list = tables ?? [];
  const full = list.length >= MAX_TABLES;
  return (
    <section className="mytables stack">
      <div className="stack" style={{ gap: 2 }}>
        <b className="vt">میزهای من</b>
        <span className="muted" style={{ fontSize: 13 }}>شهر، فرش و پشت کارتی را که داری کنار هم بچین؛ خریدشان در اپ است. در بازی، موقع ساختن اتاق زیر «بیشتر» یکی را برمی‌داری و همه‌ی سر میز همان را می‌بینند؛ اگر برنداری یا بازی سریع باشد، میز قرعه است.</span>
      </div>
      {!registered ? (
        <div className="muted"><a href={LOGIN}>وارد حساب شو</a> تا با چیزهایی که داری میزت را بسازی.</div>
      ) : (
        <div className="mt-grid">
          {list.map((t) => {
            const v = viewOf(t.table);
            return (
              <div key={t.id} className="mt-card">
                <TablePreview city={v.city?.key ?? ""} rug={v.rug.key} back={t.back} w={132} />
                <b className="mt-name">{myTableName(t)}</b>
                <span className="muted mt-sub">{[v.city ? v.city.fa : "بدون شهر", rugName(v.rug), t.back ? (byId.get(t.back)?.name ?? "پشت خاص") : "پشت پیش‌فرض"].join(" · ")}</span>
                <div className="row" style={{ gap: 6 }}>
                  <button className="chip" onClick={() => setEditing(t)}>ویرایش</button>
                  <button className="chip" onClick={() => void remove(t)} aria-label={`پاک‌کردن ${myTableName(t)}`}>پاک کن</button>
                </div>
              </div>
            );
          })}
          <button className="mt-card mt-new" onClick={() => setEditing("new")} disabled={full}>
            <span className="mt-plus" aria-hidden="true">+</span>
            <b>میز تازه</b>
            <span className="muted mt-sub">{full ? "به سقف دوازده میز رسیدی" : "شهر، فرش و پشت کارت"}</span>
          </button>
        </div>
      )}
    </section>
  );
}

/** سازنده: میزِ تازه یا ویرایشِ یکی از «میزهای من». `preset`: شهری که از ویترین آمده (با فرشِ خودش). */
function Builder({ initial, preset = "", items, busy, setBusy, registered, onDone }: {
  initial?: MyTableRow; preset?: string; items: CatalogItem[] | null; busy: string; setBusy: (s: string) => void; registered: boolean; onDone: () => void;
}) {
  const inventory = useSignal(shop.inventory);
  const tables = useCatalogTables();
  const start = viewOf(initial?.table ?? (preset || "eyvan"));
  const [city, setCity] = useState(initial || preset ? start.city?.key ?? "" : "");
  const [rug, setRug] = useState(initial || preset ? start.rug.key : "eyvan");
  const [back, setBack] = useState(initial?.back ?? "");
  const [name, setName] = useState(initial?.name ?? "");

  const byId = useMemo(() => new Map((items ?? []).map((i) => [i.id, i])), [items]);
  const has = (id: string) => (inventory.get(id) ?? 0) > 0;
  // شهرِ خاموشِ پنل فروشی نیست، ولی کسی که دارد هنوز می‌چیندش
  const cities = tables.filter((t) => t.kind === "scene" && (!t.off || has(`carpet_${t.key}`)));
  const rugs = [...tables.filter((t) => t.kind !== "scene"), ...tables.filter((t) => t.kind === "scene")]
    .filter((t) => !t.off || FREE_RUGS.includes(t.key) || has(`rug_${t.key}`) || has(`carpet_${t.key}`));

  const cityItem = (k: string) => byId.get(`carpet_${k}`);
  const rugItem = (k: string) => byId.get(`rug_${k}`);
  const ownsCity = (k: string) => !k || has(`carpet_${k}`);
  const ownsRug = (k: string) => FREE_RUGS.includes(k) || k === city || has(`rug_${k}`) || has(`carpet_${k}`);
  const ownsBack = (k: string) => !k || has(k);

  /** چیزهای انتخابیِ نداشته — تا هست، میز ذخیره نمی‌شود و دکمه‌ی پایین «در اپ بخر» است. */
  const missing: string[] = [];
  if (!ownsCity(city)) missing.push(`شهر ${tableOf(city).fa}`);
  if (!ownsRug(rug)) missing.push(rugName(tableOf(rug)));
  if (!ownsBack(back)) missing.push(byId.get(back)?.name ?? "پشت کارت");
  const unbuyable = (!ownsCity(city) && !cityItem(city)) || (!ownsRug(rug) && !rugItem(rug));
  const key = tableKey(city, rug);

  async function save() {
    if (busy || missing.length) return;
    setBusy("table");
    const r = await shop.saveMyTable(initial?.id ?? "", { name: name.trim(), table: key, back });
    setBusy("");
    if (r.error) {
      notify(r.error === "too_many" ? "به سقف دوازده میز رسیدی" : r.error === "not_owned" ? "یکی از این‌ها را هنوز نداری؛ در اپ بخرش" : "ذخیره نشد؛ دوباره امتحان کن");
      return;
    }
    notify(initial ? "میز به‌روز شد" : "میزت ساخته شد — در بازی، سر ساختن اتاق انتخابش کن");
    onDone();
  }

  const backs = (items ?? []).filter((i) => i.category === "back");

  return (
    <section className="mytable stack">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <b className="vt">{initial ? `ویرایش ${myTableName(initial)}` : "میز تازه"}</b>
        <button className="chip" onClick={onDone}>← میزهای من</button>
      </div>
      <div className="mt-head">
        <TablePreview city={city} rug={rug} back={back} />
        <div className="stack" style={{ gap: 8, minInlineSize: 0, flex: 1 }}>
          <label className="stack" style={{ gap: 4 }}>
            <span className="morelabel">نام میز</span>
            <input value={name} maxLength={24} placeholder={tableTitle(key)} onChange={(e) => setName(e.target.value)} className="mt-input" />
          </label>
          <div className="mt-sum">
            <span>{city ? `شهر ${tableOf(city).fa}` : "بدون شهر"}</span>
            <span>{rugName(tableOf(rug))}</span>
            <span>{back ? byId.get(back)?.name ?? "پشت کارت" : "پشت پیش‌فرض"}</span>
          </div>
        </div>
      </div>

      <Row title="شهر">
        <Tile on={city === ""} name="بدون شهر" note="رایگان" owned onPick={() => setCity("")} style={{ "--bg": "#14112e" } as CSSProperties} />
        {cities.map((t) => (
          <Tile key={t.key} on={city === t.key} name={t.fa} img={t.src ?? tableArt(`city-${t.key}.webp`)} cover
                note={ownsCity(t.key) ? "داری" : cityItem(t.key) ? "در اپ بخر" : "به‌زودی"} owned={ownsCity(t.key)}
                onPick={() => { if (rug === city && city) setRug(t.key); setCity(t.key); }} />
        ))}
      </Row>

      <Row title="فرش">
        {rugs.map((t) => (
          <Tile key={t.key} on={rug === t.key} name={rugName(t)} img={thumbOf(t.key)} style={{ "--bg": t.felt ?? t.bg } as CSSProperties}
                note={FREE_RUGS.includes(t.key) ? "رایگان" : ownsRug(t.key) ? (t.key === city ? "همراه شهر" : "داری") : rugItem(t.key) ? "در اپ بخر" : "به‌زودی"}
                owned={ownsRug(t.key)} onPick={() => setRug(t.key)} />
        ))}
      </Row>

      <Row title="پشت کارت">
        <Tile on={back === ""} name="پیش‌فرض" img="/art/table/back.webp" note="رایگان" owned onPick={() => setBack("")} />
        {backs.map((b) => (
          <Tile key={b.id} on={back === b.id} name={b.name || b.id} img={b.image || backArt(b.id)}
                note={ownsBack(b.id) ? "داری" : "در اپ بخر"} owned={ownsBack(b.id)} onPick={() => setBack(b.id)} />
        ))}
      </Row>

      <div className="mt-foot">
        {!registered ? (
          <div className="muted"><a href={LOGIN}>وارد حساب شو</a> تا با چیزهایی که داری میزت را بسازی.</div>
        ) : unbuyable ? (
          <button className="btn bigbtn" disabled>یکی از این‌ها هنوز فروشی نیست</button>
        ) : missing.length ? (
          <>
            <div className="muted" style={{ fontSize: 13 }}>هنوز نداری: {missing.join("، ")}. اول در اپ {missing.length > 1 ? "بخرشان" : "بخرش"}، بعد همین‌جا میزت را ذخیره کن.</div>
            <a className="btn bigbtn" href={APP_BUY}>در اپ بخر</a>
          </>
        ) : (
          <button className="btn bigbtn" disabled={!!busy} onClick={() => void save()}>
            {busy === "table" ? "…" : initial ? "ذخیره‌ی میز" : "ساختن میز"}
          </button>
        )}
      </div>
    </section>
  );
}

function Row({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <b className="morelabel">{title}</b>
      <div className="tablepick" role="radiogroup" aria-label={title}>{children}</div>
    </div>
  );
}

function Tile({ on, name, img, note, owned, cover, style, onPick }: {
  on: boolean; name: string; img?: string; note: string; owned?: boolean; cover?: boolean; style?: CSSProperties; onPick: () => void;
}) {
  return (
    <button type="button" role="radio" aria-checked={on} className={"tp" + (on ? " on" : "") + (owned ? " own" : "")} onClick={onPick}
            aria-label={`${name} — ${note}`}>
      <span className={"pic" + (cover ? " cover" : "")} style={style}>
        {img && <img src={img} alt="" width={72} height={100} loading="lazy" decoding="async" />}
      </span>
      <span className="name">{name}</span>
      <span className="note">{note}</span>
    </button>
  );
}
